// frontend/src/admin/QARichContentEditor.js
import React, { useState, useEffect } from 'react';
import AdminContentEditor from './AdminContentEditor';
import { API_BASE } from '../services/api.js';

const QARichContentEditor = ({ qaId, scope, onBack }) => {
    const [activeSection, setActiveSection] = useState('question');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [nextOrderIndex, setNextOrderIndex] = useState(0);

    // Only offer quick-add when we arrived here with scope from the main QA page
    const canQuickAdd = scope && scope.topic_id && scope.question_type;

    // Suggest the next order_index for this topic + question_type
    useEffect(() => {
        if (!canQuickAdd) return;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/predefined-qa/?topic_id=${scope.topic_id}`);
                if (!res.ok) return;
                const data = await res.json();
                const sameType = data.filter(q => q.question_type === scope.question_type);
                const maxIndex = sameType.length > 0
                    ? Math.max(...sameType.map(q => q.order_index ?? 0))
                    : -1;
                setNextOrderIndex(maxIndex + 1);
            } catch {
                // Silently keep default of 0 if this fails
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope.topic_id, scope.question_type]);

    const createNextQuestion = async () => {
        setError(null);
        setCreating(true);
        try {
            const body = {
                question: '',
                answer: '',
                question_type: scope.question_type,
                one_word_answer: null,
                detailed_explanation: null,
                past_year_reference: null,
                hindi_question: null,
                hindi_answer: null,
                hindi_detailed_explanation: null,
                parent_id: scope.parent_id ? parseInt(scope.parent_id) : null,
                order_index: parseInt(nextOrderIndex) || 0,
                subject_id: parseInt(scope.subject_id),
                chapter_id: parseInt(scope.chapter_id),
                topic_id: parseInt(scope.topic_id),
                subtopic_id: scope.subtopic_id ? parseInt(scope.subtopic_id) : null,
                chunk_id: scope.chunk_id ? parseInt(scope.chunk_id) : null,
                micro_chunk_id: scope.micro_chunk_id ? parseInt(scope.micro_chunk_id) : null,
                generate_embedding: false,   // rich types skip embeddings anyway
                has_rich_content: true
            };
            const res = await fetch(`${API_BASE}/predefined-qa/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e.detail || `HTTP ${res.status}`);
            }
            const saved = await res.json();
            // Reload this same tab onto the new QA, carrying the same scope forward
            window.location.href = `/admin/qa-rich-content/${saved.qa_id}${window.location.search}`;
        } catch (err) {
            setError(`Could not create the next question: ${err.message}`);
            setCreating(false);
        }
    };

    const tabBtn = (isActive) => ({
        padding: '6px 16px',
        border: '1px solid #ced4da',
        borderRadius: '6px',
        background: isActive ? '#007bff' : '#fff',
        color: isActive ? '#fff' : '#495057',
        fontWeight: 600,
        fontSize: '0.9em',
        cursor: 'pointer'
    });

    return (
        <div className="qarc-container">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '8px 0 12px' }}>
                {onBack && (
                    <button className="ace-back-btn" onClick={onBack}>&larr; Back</button>
                )}
                <button style={tabBtn(activeSection === 'question')} onClick={() => setActiveSection('question')}>
                    📝 Question
                </button>
                <button style={tabBtn(activeSection === 'solution')} onClick={() => setActiveSection('solution')}>
                    ✅ Solution
                </button>
                {canQuickAdd && (
                    <>
                        <input
                            type="number"
                            value={nextOrderIndex}
                            onChange={(e) => setNextOrderIndex(e.target.value)}
                            title="Order index for the new question"
                            style={{
                                marginLeft: 'auto',
                                width: '70px',
                                padding: '6px 8px',
                                border: '1px solid #ced4da',
                                borderRadius: '6px',
                                fontSize: '0.9em'
                            }}
                        />
                        <button
                            onClick={createNextQuestion}
                            disabled={creating}
                            title="Create another question in the same subject/chapter/topic and open it here"
                            style={{
                                padding: '6px 16px',
                                border: 'none',
                                borderRadius: '6px',
                                background: '#28a745',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.9em',
                                cursor: creating ? 'default' : 'pointer',
                                opacity: creating ? 0.6 : 1
                            }}
                        >
                            {creating ? 'Creating…' : '➕ New Question (same scope)'}
                        </button>
                    </>
                )}
            </div>

            {error && (
                <div style={{ background: '#fdecea', color: '#b71c1c', padding: '8px 12px',
                              borderRadius: '6px', marginBottom: '10px', fontSize: '0.9em' }}>
                    {error}
                </div>
            )}

            <div style={{ display: activeSection === 'question' ? 'block' : 'none' }}>
                <AdminContentEditor level="qa" entityId={qaId} section="question"
                    entityName={`QA #${qaId} — Question`} />
            </div>

            <div style={{ display: activeSection === 'solution' ? 'block' : 'none' }}>
                <AdminContentEditor level="qa" entityId={qaId} section="solution"
                    entityName={`QA #${qaId} — Solution`} />
            </div>
        </div>
    );
};

export default QARichContentEditor;